<template>
  <div class="sidebar">
    <div :class="[{ active: isActive }, 'sidebar-content']">
      <div class="tools" v-show="!note">
        <div class="tool-links" v-if="artwork.id">
          <FavoriteLink :artworkId="artwork.id" />
        </div>
        <div class="share-print">
          <a href class="expand" @click.prevent.stop>
            <i class="icon-forms"></i>
          </a>
          <a @click.prevent="share(artwork)" class="share" v-b-tooltip.hover.left :title="titles.share">
            <i class="icon-partager"></i>
          </a>
          <PrintLink link="artwork" :artwork="artwork" :short="false" />
          <TextNoteLink
            :resource="artwork"
            type="artwork"
            v-if="$can('create', 'text_note')"
            :title="titles.create"
            icon="Notice_New"
          ></TextNoteLink>
        </div>
      </div>
      <ArtworkSidebarDetailsIndividual
        v-if="isIndividual"
        :key="artwork.id"
        :artwork="highlightedArtwork"
        :rawArtwork="artwork"
      />
      <ArtworkSidebarDetailsNonseparable
        v-if="isNonSeparable"
        :key="artwork.id"
        :artwork="highlightedArtwork"
        :rawArtwork="artwork"
        :activeHeader="isActive"
      />
      <ArtworkSidebarDetailsSeparable
        v-if="isSeparable"
        :key="artwork.id"
        :artwork="highlightedArtwork"
        :rawArtwork="artwork"
        :activeHeader="isActive"
      />
    </div>

    <template v-if="isNonSeparable || isSeparable">
      <div class="compression-crew" :class="{ 'unfolded-all': !isFolded }">
        <div class="compression">
          <div>
            <translate>Détail des</translate>
            {{ highlightedArtwork.nb_elements }}
            <translate>éléments</translate>
          </div>
          <a class="fold" @click.prevent="toggleFolded()">
            <div class="d-none d-md-block">
              <span v-show="isFolded" v-translate>Déplier tous les éléments</span>
              <span v-show="!isFolded" v-translate>Replier les éléments</span>
            </div>
          </a>
        </div>
        <div class="crew-list">
          <ArtworkSidebarElement
            v-for="(element, index) in limitedElements"
            :element="element"
            :index="index + start"
            :current="currentIndex"
            :start="start"
            :end="end"
            :key="index"
            :count="elementCount"
            :isFolded="isFolded"
            v-on:fetchPrevious="fetchPrevious"
            v-on:fetchNext="fetchNext"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ArtworkSidebarDetailsIndividual from './ArtworkSidebarDetailsIndividual.vue';
import ArtworkSidebarDetailsSeparable from './ArtworkSidebarDetailsSeparable.vue';
import ArtworkSidebarDetailsNonseparable from './ArtworkSidebarDetailsNonseparable.vue';
import ArtworkSidebarElement from './ArtworkSidebarElement.vue';
import FavoriteLink from './FavoriteLink.vue';
import TextNoteLink from './TextNoteLink.vue';
import PrintLink from './PrintLink.vue';
import applyHighlights from 'lib/applyHighlights.js';
import abilitiesMixin from 'mixins/abilitiesMixin.js';
import shareMixin from 'mixins/shareMixin.js';
import { mapState, mapActions, mapMutations } from 'vuex';

import { elementsPerPage } from 'config';

export default {
  components: {
    ArtworkSidebarDetailsIndividual,
    ArtworkSidebarDetailsSeparable,
    ArtworkSidebarDetailsNonseparable,
    ArtworkSidebarElement,
    FavoriteLink,
    TextNoteLink,
    PrintLink,
  },
  mixins: [abilitiesMixin, shareMixin],
  data() {
    return {
      start: 0,
      end: 0,
    };
  },
  props: ['artwork'],
  mounted() {
    this.initIsFolded();
  },
  methods: {
    ...mapActions('artworks', ['fetchRelated']),
    ...mapMutations('artworks', ['assign']),
    async toggleFolded() {
      if (this.isSeparable && this.isFolded) {
        await this.fetchMore({ start: this.start, end: this.end });
      }
      this.assign({ name: 'isFolded', data: !this.isFolded });
    },
    async fetchMore({ start, end }) {
      const ids = this.artwork.related.slice(start, end);
      await this.fetchRelated({ ids: ids });
    },
    fetchPrevious() {
      const end = this.start;
      this.start = this.prepareStart(this.start - elementsPerPage);
      this.fetchMore({ start: this.start, end });
    },
    fetchNext() {
      const start = this.end;
      this.end = this.prepareEnd(this.end + elementsPerPage);
      this.fetchMore({ start, end: this.end });
    },
    initIsFolded() {
      const isFolded = this.isNonSeparable ? false : true;
      this.assign({ name: 'isFolded', data: isFolded });
      this.start = this.prepareStart(this.currentIndex - elementsPerPage);
      this.end = this.prepareEnd(this.currentIndex + elementsPerPage);
    },
    prepareStart(start) {
      return Math.max(0, start);
    },
    prepareEnd(end) {
      if (end > this.elementCount) {
        return this.elementCount;
      }

      return end;
    },
  },
  computed: {
    ...mapState('artworks', ['elements', 'isFolded']),
    limitedElements() {
      return this.elements.slice(this.start, this.end);
    },
    currentIndex() {
      const slug = this.$route.params.id;
      const id = slug?.split('-').reverse()[0];

      const elementIndex = parseInt(this.$route.query.index);
      if (typeof this.$route.query.index !== 'undefined') {
        return elementIndex;
      } else {
        return this.elements.findIndex((e) => e._id === id);
      }
    },
    isIndividual() {
      return this.artwork.type === 'individual';
    },
    isSeparable() {
      return this.artwork.type === 'separable';
    },
    isNonSeparable() {
      return this.artwork.type === 'nonseparable';
    },
    elementCount() {
      return this.elements.length;
    },
    highlightedArtwork() {
      if (!this.artwork.highlights) return this.artwork;
      return applyHighlights(this.artwork, this.artwork.highlights);
    },
    note() {
      return this.$route.query.note;
    },
    isActive() {
      const { isHeader } = this.$route.query;

      if (this.isIndividual) {
        return false;
      } else if (this.isNonSeparable) {
        return true;
      } else {
        return isHeader;
      }
    },
    titles() {
      return {
        share: this.$gettext('Partager'),
        create: this.$gettext('Créer une nouvelle notice rédactionnelle'),
      };
    },
  },
  watch: {
    artwork(newVal, oldVal) {
      if (newVal.ensemble_id !== oldVal.ensemble_id) {
        this.initIsFolded();
      }
    },
  },
};
</script>

<style lang="less">
.sidebar {
  background: @artwork-sidebar-background;
  padding: 15px;
  color: @artwork-sidebar-color;
  min-height: 100vh;
  position: relative;
}
.sidebar-content {
  background: @artwork-sidebar-content-background;
  position: relative;

  h1 {
    font-size: 16px;
    font-weight: 400;
    margin: 0;
    padding: 0;
    margin-right: 50px;
  }

  h2 {
    padding: 0;
    font-size: 16px;
    font-weight: 700;
    margin: 0 100px 5px 0;
  }

  h3 {
    font-size: 14px;
    font-weight: 400;
    margin: 0;
    padding: 0;
    margin-right: 100px;
  }

  ul.details {
    padding-left: 0px;
    margin-bottom: 0px;

    li:after {
      display: block;
      clear: both;
      content: '';
    }

    & > li {
      list-style: none;
      padding: 10px;
      border-bottom: 1px solid @artwork-sidebar-details-border;
      position: relative;

      .preview-image {
        float: right;
        margin-left: 10px;
        margin-bottom: 10px;
        cursor: pointer;
        position: relative;
        display: flex;
        flex-direction: column;
        img {
          max-width: 170px;
        }
      }

      p {
        margin: 0px;
        word-break: break-word;
      }
    }
    .not-available {
      padding: 0px;
      background-image: none;

      div {
        padding: 10px;
      }
      [class^='icon-'] {
        line-height: 1;
        float: left;
        margin: 4px 10px -3px 0;
        color: #2b2b2b;
        font-size: 13px;
      }
    }

    li .not-available {
      white-space: initial;
      padding-left: 40px;
      background-position: 0px 0px;
    }

    .yellow-highlight {
      background-color: #f5e5c4;
      display: inline-block;
      line-height: 18px;
    }
  }

  .tools {
    padding: 0px;
    border: 0px;
    position: absolute;
    top: 0px;
    right: 0px;
    z-index: 10;
  }

  .tool-links {
    border: 1px solid @artwork-sidebar-tools-border;
    position: absolute;
    top: -1px;
    right: 34px;
    padding: 0 !important;

    a {
      display: block;
      width: 34px;
      height: 34px;
      padding: 0;
      margin: 0;
      line-height: 0;
      text-align: center;
    }
  }

  .share-print {
    position: absolute;
    border: 1px solid @artwork-sidebar-tools-border;
    top: -1px;
    right: -1px;
    padding: 0 !important;
    z-index: 100;
    background-color: @artwork-sidebar-tools-background;

    &:hover {
      border: 2px solid @artwork-sidebar-share-print-border;
      top: -2px;
      right: -2px;
      display: flex;
      flex-direction: column;
      a {
        display: block;
      }
    }

    a.expand {
      display: block;
      width: 34px;
      height: 34px;
      text-align: center;
      padding: 0;
      margin: 0;
      line-height: 0;
      .icon-forms {
        font-size: 28px;
        line-height: 36px;
      }
    }

    a {
      display: none;
      width: 34px;
      height: 34px;
      text-align: center;
      padding: 0;
      margin: 0;
      line-height: 0;
    }
  }
}

.compression-crew {
  background-image: url(../../img/compression-crew-bg.png);
  background-position: left;
  background-repeat: repeat-y;
}
.compression {
  background-color: @compression-background;
  margin: 6px 0 0 0;
  padding: 10px 10px 10px 25px;
  background-image: url(../../img/icon-compression-bg.png);
  background-position: left;
  background-repeat: no-repeat;
  display: flex;
  justify-content: space-between;

  a {
    color: @compression-link-color;
    padding-right: 60px;
    background-image: url(../../img/icon-compression.png);
    background-position: right;
    background-repeat: no-repeat;
  }
}

.unfolded-all {
  .crew-next-page {
    margin-left: 33px;
  }
  .crew-element {
    padding-top: 6px;
    &.folded {
      padding-top: 0;
    }
  }
  .crew-element:not(.folded) + .gap {
    display: none;
  }
  .crew-next,
  .crew-prev {
    display: none;
  }
}

@media (max-width: 767px) {
  .sidebar-content {
    .share-print {
      a {
        display: none;
        width: 34px;
        height: 34px;
        text-align: center;
        padding: 0;
        margin: 0;
        line-height: 0;
      }
    }
  }
}

@media (min-width: 992px) {
  .sidebar {
    min-height: 100vh;
  }
  .sidebar-content {
    &.active {
      border-top: 2px solid @artwork-sidebar-active-border;
      margin-right: -15px;
    }
  }
}

@media (max-width: 991px) {
  .sidebar {
    height: auto;
  }
}

.sidebar-content .details.not-available [class^='icon-'],
.sidebar-content .details.not-available [class*=' icon-'] {
  font-size: 16px;
  float: left;
  margin-right: 10px;
  line-height: 1;
}
</style>
